import {
  HeadsetIcon,
  QuestionIcon,
  TermsIcon,
  LockIcon,
  CookieIcon,
  HowItWorksIcon,
  GlobeIcon,
  UsersIcon,
  FeaturesIcon,
} from "./icons";

export const categories = [
  { id: "contact", label: "Contact Us", icon: HeadsetIcon },
  { id: "faqs", label: "FAQs", icon: QuestionIcon },
  { id: "how-it-works", label: "How It Works", icon: HowItWorksIcon },
  { id: "features", label: "Features", icon: FeaturesIcon },
  { id: "who-podus-is-for", label: "Who Podus is for", icon: UsersIcon },
  { id: "service-coverage", label: "Service Coverage", icon: GlobeIcon },
  { id: "terms", label: "Terms & Condition", icon: TermsIcon },
  { id: "privacy", label: "Privacy Policy", icon: LockIcon },
  { id: "cookie-policy", label: "Cookie Policy", icon: CookieIcon },
];

export interface FaqItem {
  question: string;
  answer: string;
}

export const paymentMethods = [
  { name: "Credit / Debit Card", description: "Visa, Mastercard, and other major cards accepted." },
  { name: "Bank Transfer", description: "Direct transfer from your bank account." },
  { name: "Mobile Money", description: "Pay using your mobile money wallet." },
  { name: "PayPal", description: "Secure payments via your PayPal account." },
];

export const generalFaqs: FaqItem[] = [
  { question: "Is Podus AI free?", answer: "Podus AI is free to use for anybody willing to get legal insight." },
  { question: "How do I get started?", answer: "Simply sign up for an account, and you can start chatting with our AI assistant. You can ask legal questions, create cases, and access various legal resources." },
  { question: "Does Podus AI replace Lawyers?", answer: "Podus AI does not replace the position of lawyers in solving legal cases. It rather acts as a first aid for legal problems." },
  { question: "Is PodusAI only for lawyers?", answer: "No. PodusAI serves both lawyers and everyday Nigerians. Whether you need to identify government agencies, understand your rights, or get initial legal guidance, PodusAI is built for active citizenship." },
  { question: "As a non-lawyer, what can PodusAI help with?", answer: "PodusAI helps you report human rights violations and connect with pro bono lawyers; access Police PRO numbers for all 36 states in cases of brutality; Draft petitions, FOI requests, and complaint letters; Get legal guidance in plain terms." },
  { question: "Does PodusAI support Nigerian languages?", answer: "Yes. You can ask questions in Yoruba, Hausa, and Igbo, and PodusAI will respond in the same language. We also offer voice features for users with visual or hearing disabilities." },
  { question: "Can PodusAI connect me with lawyers?", answer: "Yes. If your case involves human rights violations or gender-based abuse, we'll assess it and assign you a pro bono lawyer where applicable. Terms and conditions apply." },
  { question: "Can I trust PodusAI over general AI tools?", answer: "Absolutely. Unlike general AI tools, PodusAI is trained specifically on Nigerian laws and precedents. PodusAI provides verifiable sources and citations for every legal reference, not just section numbers. You can download and confirm the accuracy yourself, protecting you from AI hallucinations." },
  { question: "How often do you update your legal database?", answer: "We update quarterly. Our latest version 4.0 includes 800+ Nigerian legislations, 40,000+ Supreme & Appeal Court cases, Downloadable responses in Word format, State-specific Police PRO numbers & emergency helplines of national agencies." },
  { question: "How secure is my personal information?", answer: "All personal data is encrypted following optimal data privacy practices. View our Privacy Policy here." },
  { question: "How many prompts can I use per day?", answer: "All users are allotted 6 free prompts daily." },
  { question: "How can I send feedback?", answer: "We welcome your feedback! Email us at hello@gavel.ng and we'll respond promptly." },
  { question: "What devices can I use PodusAI on?", answer: "PodusAI is accessible on any device via our web app or WhatsApp (with fewer prompts). Click the chat icon in the top-left corner to get started." },
];

export interface HowItWorksStep {
  title: string;
  description: string;
}

export const howItWorksSteps: HowItWorksStep[] = [
  {
    title: "Lodge A Complaint",
    description: "To lodge a complaint on our platform, provide accurate information about your case including nature of complaint, details of persons involved, location, date, using the 'create a case' link.",
  },
  {
    title: "Sign Up",
    description: "Its fast and easy!",
  },
  {
    title: "Pro Bono Lawyers Will Reach You",
    description: "We will link these cases with pro-bono lawyers within your vicinity to help you achieve justice.",
  },
  {
    title: "Make Donations",
    description: "Donate tokens for the services rendered to the selected indigent person by clicking 'pay' button beside follow case. You can be a good Samaritan for a person in need of urgent legal support.",
  },
];

export interface Feature {
  title: string;
  description: string;
}

export const platformFeatures: Feature[] = [
  {
    title: "AI-Powered Legal Assistant",
    description: "Ask legal questions in plain language and get instant, AI-powered answers backed by real legislation and court cases. Available in English, Yoruba, Igbo, and Hausa.",
  },
  {
    title: "Contract Management",
    description: "Draft, review, and manage legal contracts with AI-powered analysis and expert review by a lawyer in the relevant field. A single unclear clause can cost your business months of revenue — Podus Contract helps prevent that.",
  },
  {
    title: "Background Checks",
    description: "Yourchecka reduces hiring risk with structured background checks on employment, criminal records, social media, education, and guarantors. Hire with confidence.",
  },
  {
    title: "Pro Bono Legal Aid",
    description: "Legal help shouldn't depend on your income. Access free representation and connect with pro bono lawyers for human rights violations, gender-based abuse, and other qualifying cases.",
  },
  {
    title: "Lawyer Appointments",
    description: "Book appointments with verified legal professionals for consultations, case reviews, and ongoing legal representation. Find the right lawyer for your specific legal needs.",
  },
  {
    title: "Dispute Resolution",
    description: "File, track, and resolve disputes through digital court-connected resolution via Podus Multidoor. A faster, more accessible alternative to traditional litigation.",
  },
  {
    title: "Multi-Language Support",
    description: "Ask questions in Yoruba, Hausa, and Igbo, and Podus AI responds in the same language. Voice features are also available for users with visual or hearing disabilities.",
  },
  {
    title: "Single Sign-In Ecosystem",
    description: "One account gives you access to all Podus products — AI assistant, contract management, background checks, legal aid, appointments, and dispute resolution.",
  },
];

export interface AudienceSegment {
  number: string;
  title: string;
  description: string;
}

export const audienceSegments: AudienceSegment[] = [
  {
    number: "01",
    title: "Individuals & Citizens",
    description: "Access clear legal guidance, draft documents, resolve disputes, and connect with lawyers. Podus helps individuals understand their rights and take action without navigating complicated legal systems alone.",
  },
  {
    number: "02",
    title: "Businesses & Entrepreneurs",
    description: "Draft and analyze contracts, track obligations, and conduct employee background checks. Podus helps businesses reduce legal risk, make informed decisions, and manage agreements with greater clarity and confidence.",
  },
  {
    number: "03",
    title: "Lawyers, Legal Students & Professionals",
    description: "Offer consultations, review contracts, support dispute resolution, and participate in legal aid. Podus connects legal professionals with users while creating opportunities to build experience and expand professional impact.",
  },
  {
    number: "04",
    title: "NGOs & Human Rights Organizations",
    description: "Support communities, document rights violations, and connect affected individuals to legal support. Podus provides digital tools that help NGOs strengthen advocacy, manage cases, and scale justice initiatives.",
  },
];

export interface CoverageRegion {
  country: string;
  flag: string;
  status: "active" | "expanding";
  services: string[];
  description: string;
}

export const coverageRegions: CoverageRegion[] = [
  {
    country: "Nigeria",
    flag: "\uD83C\uDDF3\uD83C\uDDEC",
    status: "active",
    services: [
      "Podus AI Legal Assistant",
      "Podus Contract Management",
      "Yourchecka (Background Checks)",
      "Podus Aid (Pro Bono Legal Support)",
      "Podus Appointment (Lawyer Booking)",
      "Podus Multidoor (Dispute Resolution)",
    ],
    description: "Full access to all Podus services. Our legal database includes 800+ Nigerian legislations and 40,000+ Supreme & Appeal Court cases. Support available in English, Yoruba, Igbo, and Hausa.",
  },
  {
    country: "Ghana",
    flag: "\uD83C\uDDEC\uD83C\uDDED",
    status: "expanding",
    services: [
      "Podus AI Legal Assistant",
      "Podus Contract Management",
      "Podus Appointment (Lawyer Booking)",
    ],
    description: "Growing coverage with AI-powered legal guidance and contract management. Services are adapted to Ghanaian legal frameworks and regulations.",
  },
  {
    country: "United Kingdom",
    flag: "\uD83C\uDDEC\uD83C\uDDE7",
    status: "active",
    services: [
      "Podus AI Legal Assistant",
      "Podus Contract Management",
      "Yourchecka (Background Checks)",
      "Podus Appointment (Lawyer Booking)",
    ],
    description: "Full compliance with UK GDPR and UK General Data Protection Regulation 2018. Services tailored to UK legal standards with localized terms and privacy policies.",
  },
];

export const tutorialVideos = [
  { title: "Lorem ipsum dolor sit amet", duration: "38:24" },
  { title: "Lorem ipsum dolor sit amet", duration: "38:24" },
  { title: "Lorem ipsum dolor sit amet", duration: "38:24" },
  { title: "Lorem ipsum dolor sit amet", duration: "38:24" },
];

export const termsContent = `Lorem ipsum dolor sit amet consectetur. Ultrices sed vestibulum neque elit velit nunc ante fermentum volutpat. Condimentum adipiscing ac commodo ut ultrices erat sit mi eu. Lobortis mi proin elit enim nisi vel ac. Risus orci ullamcorper felis sed. In fermentum leo sed maecenas posuere suspendisse dolor nulla duis. Turpis erat aliquet sed auctot velit quis sit a purus. Vitae bibendum maecenas gravida id adipiscing ac faucibus. Nunc commodo pellentesque risus in tristique fermentum.

Lorem aliquet mattis sapien lectus vulputate odio. Elit aliquet felis porttitor eu mi. Ipsum posuere nibh nisl mauris enim morbi laoreet consectetur. Feugiat sodales morbi porttitor risus ornare rhoncus imperdiet fermentum sollicitudin. Sit donec quis vestibulum facilisis venenatis mauris sollicitudin volutpat arcu. Cras integer amet magnis quis. A velit elit duis consectetur ornare amet bibendum consectetur feugiat. Quis enim aliquet bibendum dolor aenean. Neque turpis nulla fringilla proin.

Fermentum mi duis odio lorem a. Sed libero malesuada sed vitae arcu nulla. Sit egestas purus libero commodo in enim. Lacinia hendrerit arcu consectetur vitae lectus nibh. Velit leo mi at proin nisi ac massa amet. Purus eleifend habitasse magnis eu sed. Congue sagittis mattis ullamcorper ut ac.

Tristique sit id cursus egestas id. Mauris in laoreet dictumst est varius nulla mauris enim. Commodo id una molestie elit leo pellentesque. Turpis blandit mattis id adipiscing facilisi ut posuere nisi velit integer amet et nibh. Penatibus cursus viverra orci aenean pretium semper enim. Turpis nulla id urna vel commodo amet volutpat potenti. Ama posuere consequat ligula enim nulla sociis varius quis. Molestie et donec dapibus lobortis. Nisi mauris ullamcorper mattis integer dui libero.

Diam non tempus nunc et aliquet sit quam pellentesque. A vulputate feugiat at orci pharetra fames lacuts. Molestie quis diam volutpat tellus lorem venenatis adipiscing. Integer sit sagittis consectetur eu non ipsum sit aenean neque. Scelerisque ante at a nunc scelerisque egestas nullam viverra euismod. Ac et amet quam leo luctus amet aliquet. Turpis nisi tristique eget sed tortor sed faucibus nec sodales. In mi duis mattis ac sagittis turpis gravida. A suspendisse facilisis rutrum eu. Volutpat elit posuere tempor euismod sit. Tempus quis nunc pellentesque lorem ut ornare urna. Velit sit sed scelerisque amet.

Consectetur mauris facilisi faucibus aenean interdum et. Faucibus quam tempus libero eu etiam semper eget lectus. Malesuada interdum lorem tempor neque ut enim justo. Donec sit malesuada sed neque netus curabitur. Nunc aliqualm pretium nec dolor ultrices sit dignissim tempus dignissim. Mauris interdum vitae donec interdum tortor tortor lectus justo.

Condimentum quis enim tortor tincidunt egestas non purus in pharetra. Id sit varus sit viverra laoreet. Pellentesque duis fermentum convallis nisi orci eu. Una cursus in vel magna turpis semper odio diam tempus. Ullamcorper ullamcorper in ultrices vestibulum arcu ipsum dui. Eget nisi facilisis ullamcorper imperdiet praesent pellentesque massa in sed. Feugiat tempus nullam velit massa ornare ut scelerisque tortor vel. Blandit sed sit a adipiscing. Purus velit morbi tortor ut laoreet scelerisque arcu lacus tellus. Felis consequat faucibus non lacus felis. Vel sed rhoncus id augue. Donec etiam elit habitant integer tortor tristique. Lorem in est adipiscing eget dolor nisi habitasse. Laoreet enim metus turpis nec lectus eget tempus vestibulum in.`;

export const privacyContent = `At Podus AI, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.

We collect information that you provide directly to us, including your name, email address, phone number, and payment information when you create an account or use our services. We also automatically collect certain information when you access our platform, including your IP address, browser type, operating system, and usage data.

We use the information we collect to provide, maintain, and improve our services, to process transactions and send related information, to communicate with you about products, services, and events, and to comply with legal obligations.

We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, access controls, and regular security assessments.

We do not sell your personal information to third parties. We may share your information with service providers who assist us in operating our platform, conducting our business, or serving our users, so long as those parties agree to keep this information confidential.

You have the right to access, correct, or delete your personal information at any time. You may also opt out of receiving marketing communications from us. To exercise these rights, please contact our support team.

We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the effective date.`;
